import React from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";

import { useLocation, useNavigate } from "react-router-dom";
import { MaterialIcons } from "../../icons/Material/MaterialIcons";

import { BreadcrumbsBarType } from "../../types";
import { navLabelKey } from "../../i18n/useNavLabel";

const SEGMENT_KEY: Record<string, string> = {
  empresa: "breadcrumbSegment.empresa",
  usuario: "breadcrumbSegment.usuario",
  periodo: "breadcrumbSegment.periodo",
  novedad: "breadcrumbSegment.novedad",
  tipodeperiodo: "breadcrumbSegment.tipodeperiodo",
  tipodenovedad: "breadcrumbSegment.tipodenovedad",
};

const BreadcrumbsBar: React.FC<BreadcrumbsBarType> = ({
  empresa,
  empresaparametro,
  icon,
}) => {
  const { t } = useTranslation("layout");
  const location = useLocation();
  const navigate = useNavigate();

  const firstCrumb = location.pathname.split("/")[1];
  const secondCrumb = location.pathname.split("/")[2];
  const thirdCrumb = empresaparametro
    ? location.pathname.split("/")[4]
    : location.pathname.split("/")[3];

  const Icon = MaterialIcons[icon || "PollIcon"];

  // Los segmentos vienen crudos de la URL (ej. "empresa", "tipodeperiodo") y
  // no tienen idioma propio: se traducen vía SEGMENT_KEY, con fallback a la
  // capitalización simple de siempre para cualquier segmento no mapeado.
  const translateSegment = (segment: string) => {
    const key = SEGMENT_KEY[segment.toLowerCase()];
    if (key) {
      return t(key);
    }
    return segment.charAt(0).toUpperCase() + segment.slice(1);
  };

  const firstCrumbCap = (() => {
    const key = navLabelKey(`/${firstCrumb}`);
    return key ? t(key) : translateSegment(firstCrumb);
  })();
  const thirdCrumbCap = translateSegment(thirdCrumb);

  const handleSecondCrumb = () => translateSegment(secondCrumb);

  const handleBreadcrumbClick = (e: any) => {
    e.preventDefault();
    navigate(`/${firstCrumb}`);
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-start",
        paddingBottom: "12px",
      }}
    >
      <div role="presentation">
        <Breadcrumbs
          separator={<MaterialIcons.ArrowForwardIcon fontSize="small" />}
          aria-label="breadcrumb"
        >
          <Link
            underline="none"
            sx={{ color: "black", display: "flex", placeItems: "center" }}
            href={`/${firstCrumb}`}
            role="button"
            onClick={(e: any) => handleBreadcrumbClick(e)}
          >
            <Icon sx={{ marginRight: "20px" }} />
            {firstCrumbCap}
          </Link>

          {!empresa && (
            <Typography key="3" sx={{ color: "black", textAlign: "center" }}>
              {handleSecondCrumb()}
            </Typography>
          )}

          {empresa && (
            <Link
              underline="none"
              sx={{ color: "black", display: "flex", placeItems: "center" }}
              href={`/${firstCrumb}`}
            >
              {handleSecondCrumb()}
            </Link>
          )}

          {(empresa || empresaparametro) && (
            <Typography key="3" sx={{ color: "black", textAlign: "center" }}>
              {thirdCrumbCap}
            </Typography>
          )}
        </Breadcrumbs>
      </div>
    </Box>
  );
};

export default BreadcrumbsBar;
